import {gql} from '@apollo/client';
import {css} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React, {useMemo, useState} from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  ProcedureRunDataEntryMode,
  ProcedureType,
  StudyType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ProcedureRunPlanConditionREST, updateProcedureRunPlan} from 'procedures/network/procedureRunPlanDAO';
import buildFullExperimentPlan from 'procedures/planning/buildFullExperimentPlan';
import {FullExperimentPlan} from 'procedures/planning/ProcedurePlanTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {
  getPowAgnosticProcedureFromFlowchartVersion,
  getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan,
} from 'procedures/utils/powAgnosticFlowchartHelpers';
import {Condition} from 'studies/typed/process-development/useProcessDevelopmentStudyState';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FlowchartSidebar from 'ui-library/reactflow/sidepanels/FlowchartSidebar';

import {StudyInaccessibleContentBanner} from '../utils/ui/StudyQueryBanners';
import {
  MultiProcedureRunPlanStudyDesignTabContentsQuery,
  MultiProcedureRunPlanStudyDesignTabContentsQueryVariables,
  MultiProcedureRunPlanStudyDesignTabContentsQuery_procedureRunPlan_ProcedureRunPlanNode as ProcedureRunPlanNode,
} from './__generated/MultiProcedureRunPlanStudyDesignTabContentsQuery';
import getStudyDesignFromProcedureRunPlan, {
  STUDY_DESIGN_FROM_PROCEDURE_RUN_PLAN_FRAGMENT,
} from './getStudyDesignFromProcedureRunPlan';
import {
  areProcedureRunInputsEvaluatedValuesValid,
  isNumConditionReplicatesValid,
  validateConditions,
} from './procedureRunStudyValidationUtils';
import SelectedProcedureEditor from './SelectedProcedureEditor';
import StudyDesignTabSavingButtons, {StudyDesignTabEditState} from './StudyDesignTabSavingButtons';
import {
  ContextPanel,
  PARAMETER_VALUES_TAB_NAME,
  ParameterValuesTabContents,
  SETTINGS_TAB_NAME,
  SettingsTabContents,
  studyDesignTabStyles,
} from './StudyDesignTabSharedComponents';
import {useStudyDesignState} from './useStudyDesignState';

// Indices for accessing typed values in the experiment plan
const MATERIAL_INDEX = 0;
const VALUE_INDEX = 0;

const AMOUNT_INDEX = 1;

/**
 * Serializes experiment plan conditions to the procedure run PLAN REST format.
 * Note: This is different from serializeExperimentConditionsForNetwork which is for procedure RUNS.
 *
 * Plan REST API expects {value: TypedValue} for all types (data params, material inputs, equipment).
 * For material inputs, we nest both material and amount inside the value object.
 */
function serializePlanConditionsForNetwork(
  fullExperimentPlan: FullExperimentPlan,
  conditions: Array<Condition>
): Array<ProcedureRunPlanConditionREST> {
  const {replicatesPerCondition, namesPerCondition, descriptionsPerCondition, experimentPlanPerCondition} =
    fullExperimentPlan;

  return experimentPlanPerCondition.map((methodPlans, i) => ({
    // Only include id for existing conditions; omit for new conditions so backend creates them
    ...(conditions[i].id != null ? {id: conditions[i].id} : {}),
    name: namesPerCondition[i],
    description: descriptionsPerCondition[i],
    position: i,
    replicateInfos: Array.from({length: replicatesPerCondition[i]}, (_, j) => ({
      replicateNumber: j + 1,
    })),
    methodPlans: methodPlans.map((methodPlan) => ({
      methodVersionId: methodPlan.methodId,
      dataParameterValues: methodPlan.parameterValues.map((pv) => ({
        dataParameterVersionId: pv.sourceId,
        values: [{value: pv.values[VALUE_INDEX]}],
      })),
      materialInputValues: methodPlan.materialInputs.map((pv) => ({
        materialInputVersionId: pv.sourceId,
        // Store material and amount nested inside value object
        values: [
          {
            value: {
              material: pv.values[MATERIAL_INDEX],
              amount: pv.values[AMOUNT_INDEX],
            },
          },
        ],
      })),
      equipmentValues: methodPlan.equipmentInputs.map((pv) => ({
        equipmentVersionId: pv.sourceId,
        // Store equipment inside value object
        values: [{value: {equipment: pv.values[VALUE_INDEX]}}],
      })),
    })),
  }));
}

export const MULTI_PROCEDURE_RUN_PLAN_STUDY_DESIGN_TAB_CONTENTS_QUERY: DocumentNode = gql`
  query MultiProcedureRunPlanStudyDesignTabContentsQuery($procedureRunPlanId: StrictString!)
  @skipAudit
  @engComponent(name: STUDIES) {
    procedureRunPlan(id: $procedureRunPlanId) {
      ...GetStudyDesignFromProcedureRunPlanGraphQL
      ... on ProcedureRunPlanNode {
        id
        dataEntryMode
        procedureRunPlanConditions {
          id
          name
          description
          position
        }
        templateProcedureFlowchartVersion {
          ... on ProcedureFlowchartVersionNode {
            id
            largestTableTypeSizes {
              tableKind
              size
            }
            procedureFlowchart {
              id
              parent {
                id
              }
            }
          }
          ...InaccessibleInfoGraphQL
        }
        procedureFlowchartConfigVersionProxy {
          ... on ProcedureFlowchartConfigVersionProxyNode {
            id
            largestTableTypeSizes {
              tableKind
              size
            }
            procedure {
              id
            }
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${STUDY_DESIGN_FROM_PROCEDURE_RUN_PLAN_FRAGMENT}
`;

function MultiProcedureRunPlanStudyDesignTabContents(props: {
  procedureRunPlan: ProcedureRunPlanNode;
  studyType: StudyType;
  studyId: string;
  procedureType: ProcedureType;
  refetch: () => void;
}): JSX.Element {
  const {procedureRunPlan, procedureType} = props;

  const {experimentalInputs: initialExperimentalInputs, templateFlowchart} = useMemo(
    () => getStudyDesignFromProcedureRunPlan(procedureRunPlan),
    [procedureRunPlan]
  );

  // always have at least 1 condition
  const initialConditions: Array<Condition> = useMemo(
    () =>
      procedureRunPlan.procedureRunPlanConditions.length > 0
        ? [...procedureRunPlan.procedureRunPlanConditions]
            .sort((a, b) => a.position - b.position)
            .map((condition) => ({
              id: condition.id,
              numReplicates: 1,
              name: condition.name,
              description: condition.description ?? undefined,
            }))
        : [
            {
              id: '',
              numReplicates: 1,
              name: 'Condition 1',
              description: undefined,
            },
          ],
    [procedureRunPlan]
  );

  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan(procedureRunPlan);
  assert(flowchartVersion != null && !isInaccessible(flowchartVersion), 'Expected flowchart version');
  const procedureId = getPowAgnosticProcedureFromFlowchartVersion(flowchartVersion).id;

  // Editable data entry mode state
  const [dataEntryMode, setDataEntryMode] = useState<ProcedureRunDataEntryMode>(
    procedureRunPlan.dataEntryMode
  );
  const [initialDataEntryMode] = useState<ProcedureRunDataEntryMode>(procedureRunPlan.dataEntryMode);

  const {
    selectedMethodId,
    setSelectedMethodId,
    editState,
    setEditState,
    selectedDesignTabIndex,
    setSelectedDesignTabIndex,
    sidepanelName,
    setSidepanelName,
    conditions,
    setConditions,
    experimentalInputs,
    setExperimentalInputs,
    resetConditionsAndInputs,
    context,
    methodExecutionLimitState,
    editNumColsButtons,
  } = useStudyDesignState({
    isPlan: true,
    initialExperimentalInputs,
    templateFlowchart,
    initialConditions,
    procedureType,
    flowchartVersion,
    dataEntryMode,
    procedureId,
  });

  async function handleSave(): Promise<void> {
    setEditState(StudyDesignTabEditState.SAVING);
    assert(conditions != null, 'Conditions should not be null for plan');
    assert(experimentalInputs != null, 'Experimental inputs should not be null for plan');
    assert(context != null, 'Expected context to be non-null');

    try {
      // Build full experiment plan from current state
      const replicatesPerCondition = conditions.map((condition) => condition.numReplicates);
      const namesPerCondition = conditions.map((condition) => condition.name);
      const descriptionsPerCondition = conditions.map((condition) => condition.description);
      const experimentPlan = buildFullExperimentPlan({
        replicatesPerCondition,
        namesPerCondition,
        descriptionsPerCondition,
        numConditions: conditions.length,
        experimentalInputs,
        evaluatedValues: context.evaluatedValues,
      });

      // Serialize to plan-specific REST format (different from procedure runs)
      const conditionsForUpdate = serializePlanConditionsForNetwork(experimentPlan, conditions);

      await updateProcedureRunPlan({
        id: procedureRunPlan.id,
        studyId: props.studyId,
        experimentConditions: conditionsForUpdate,
        dataEntryMode,
      });

      // Refetch to get updated data
      props.refetch();
      setEditState(StudyDesignTabEditState.SAVED);
    } catch (error) {
      // On error, go back to editing state so user can retry
      setEditState(StudyDesignTabEditState.EDITING);
      throw error;
    }
  }

  function handleCancel(): void {
    setEditState(StudyDesignTabEditState.SAVED);
    // Use resetConditionsAndInputs to atomically reset both conditions and experimental inputs
    // This avoids stale closure issues when calling setConditions and setExperimentalInputs separately
    resetConditionsAndInputs(initialConditions, initialExperimentalInputs);
    setDataEntryMode(initialDataEntryMode);
  }

  return selectedMethodId == null ? (
    <div className={css(studyDesignTabStyles.bufferTabWrapper)}>
      <InlineBanner
        bannerType={InlineBannerType.INFO}
        text={`No ${getProcedureUIString(ProceduresStringKeys.METHOD, procedureType, {
          stringCase: StringCase.LOWER,
          plural: true,
        })} have been configured for this ${getProcedureUIString(
          ProceduresStringKeys.PROCEDURE,
          procedureType,
          {stringCase: StringCase.LOWER}
        )}.`}
      />
    </div>
  ) : (
    <div
      className={css(studyDesignTabStyles.designBackground)}
      {...getTestProps({component: 'MultiProcedureRunPlanStudyDesignTabContents', element: 'root'})}
    >
      <div className={css(studyDesignTabStyles.header)}>
        <div className={css(studyDesignTabStyles.tabTitle)}>Design</div>
        <div className={css(studyDesignTabStyles.bannerAndButtons)}>
          {editState === StudyDesignTabEditState.EDITING && (
            <InlineBanner bannerType={InlineBannerType.INFO} text="Editing procedure run plan design." />
          )}
          <StudyDesignTabSavingButtons
            onCancel={handleCancel}
            onEdit={() => {
              setEditState(StudyDesignTabEditState.EDITING);
            }}
            onSave={handleSave}
            editState={editState}
            isSaveDisabled={
              !areProcedureRunInputsEvaluatedValuesValid(context.evaluatedValues) ||
              !validateConditions(conditions) ||
              !isNumConditionReplicatesValid(methodExecutionLimitState)
            }
          />
        </div>
      </div>
      <div className={css(studyDesignTabStyles.leftSidepanelContainer)}>
        <FlowchartSidebar
          icons={[
            {
              name: 'note-table',
              onClick: () => {
                setSidepanelName(PARAMETER_VALUES_TAB_NAME);
              },
            },
            {
              name: 'settings',
              onClick: () => {
                setSidepanelName(SETTINGS_TAB_NAME);
              },
            },
          ]}
          selectedIconIndex={selectedDesignTabIndex}
          setSelectedIconIndex={setSelectedDesignTabIndex}
        />
        <ContextPanel
          sidepanelName={sidepanelName}
          sidepanelHeaderContent={
            selectedDesignTabIndex === 0 && editState === StudyDesignTabEditState.EDITING
              ? (editNumColsButtons as JSX.Element)
              : undefined
          }
        >
          {selectedDesignTabIndex === 0 ? (
            <ParameterValuesTabContents
              methods={templateFlowchart.nodes}
              procedureFlowchart={templateFlowchart}
              selectedMethodId={selectedMethodId}
              onChangeSelectedMethod={setSelectedMethodId}
              procedureType={procedureType}
              context={context}
            />
          ) : (
            <>
              <SettingsTabContents
                conditions={conditions}
                setConditions={setConditions}
                selectedMethodId={selectedMethodId}
                experimentalInputs={experimentalInputs}
                setExperimentalInputs={setExperimentalInputs}
                locked={editState !== StudyDesignTabEditState.EDITING}
                procedureType={procedureType}
                methodExecutionLimitState={methodExecutionLimitState}
                studyType={props.studyType}
                dataEntryMode={dataEntryMode}
                estimateResult={{stale: false, executionPlan: null}}
                setDataEntryMode={setDataEntryMode}
                isPlan={true}
              />
              <SelectedProcedureEditor
                isEditable={editState === StudyDesignTabEditState.EDITING}
                procedureId={procedureId}
                flowchartVersionId={flowchartVersion.id}
                procedureType={procedureType}
                setProcedureId={() => {}}
              />
            </>
          )}
        </ContextPanel>
      </div>
    </div>
  );
}

export default function QueryingMultiProcedureRunPlanStudyDesignTabContents({
  procedureRunPlanId,
  studyType,
  studyId,
  procedureType,
}: {
  procedureRunPlanId: string;
  studyType: StudyType;
  studyId: string;
  procedureType: ProcedureType;
}): JSX.Element {
  const result = useBenchlingQuery<
    MultiProcedureRunPlanStudyDesignTabContentsQuery,
    MultiProcedureRunPlanStudyDesignTabContentsQueryVariables
  >(MULTI_PROCEDURE_RUN_PLAN_STUDY_DESIGN_TAB_CONTENTS_QUERY, {variables: {procedureRunPlanId}});

  if (result.type !== QueryResultType.SUCCESS) {
    return <CenteredHorizontalThrobber />;
  }

  const {data, refetch} = result;
  if (isInaccessible(data.procedureRunPlan)) {
    return (
      <StudyInaccessibleContentBanner
        inaccessibleInfo={data.procedureRunPlan}
        modelType={ModelTypeConstants.PROCEDURE_RUN_PLAN}
        displayErrorText="This procedure run plan is no longer accessible."
      />
    );
  }

  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan(data.procedureRunPlan);
  if (isInaccessible(flowchartVersion)) {
    return (
      <StudyInaccessibleContentBanner
        inaccessibleInfo={flowchartVersion}
        modelType={ModelTypeConstants.PROCEDURE}
        displayErrorText="The design of this procedure run plan is not accessible."
      />
    );
  }

  return (
    <MultiProcedureRunPlanStudyDesignTabContents
      procedureRunPlan={data.procedureRunPlan}
      studyType={studyType}
      studyId={studyId}
      procedureType={procedureType}
      refetch={refetch}
    />
  );
}
